import {
    ChangeEvent,
    FormEvent,
    useEffect,
    useMemo,
    useRef,
    useState
} from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";

import useCheckoutQueryParams from "@hooks/useCheckoutQueryParams";

import useAppStateSync from "@/hooks/useAppStateSync";
import {
    faChevronLeft,
    faCircleCheck,
    faCircleXmark
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useAtom } from "jotai";

import {
    Box,
    Flex,
    FormControl,
    FormLabel,
    Input,
    InputGroup,
    InputRightElement
} from "@chakra-ui/react";

import { CheckoutPageRoutesEnum } from "@ts/enums/CheckoutPageRoutesEnum";
import { ClassesEnum } from "@ts/enums/ClassesEnum";
import { PLAN_TYPE } from "@ts/enums/PlanTypesEnum.ts";

import {
    checkoutAtom,
    checkoutTotalAmountAtom,
    couponAtom,
    createSubscriptionAtom,
    isPaymentPendingAtom,
    pageQueryParamsAtom,
    validateCouponAtom
} from "@atoms/checkoutAtom.ts";

import { formatPrice, getTrackingId } from "@utils/utils";

import { Alert } from "@components/Alert";
import { Button } from "@components/Button";
import { Collapsable } from "@components/Collapsable";
import { Text } from "@components/Text";

import { PriceIdApiInterface } from "../PaymentCaptureContainer/PaymentCaptureContainer";
import CheckoutSummary from "./components/CheckoutSummary";

export interface CheckoutSummaryItem {
    name: string;
    price: number;
    grade?: string;
    currency?: string;
    isAlreadyPurchased?: boolean;
    isEditable: boolean;
    isDiscountApplied: boolean;
    reducedPrice: number;
}

export interface CheckoutSummaryDiscount {
    appliedOn: "each_specified_item" | "invoice";
    code: string;
    name: string;
    description: string;
    invoiceName: string | null;
    discountType: "percentage" | "fixed";
    durationType: "one_time" | "forever" | "limited_period";
    durationLimit: string | null;
    discountPercentage: number | null;
    discountAmount: number | null;
    totalReducedPrice: number;
    isEditable: boolean;
}

export interface CheckoutInfoInterface {
    product: CheckoutSummaryItem;
    addon: CheckoutSummaryItem | null;
    discount: CheckoutSummaryDiscount | null;
}

interface PropTypes {
    onApplyHundredPercentOff: (s: boolean) => void;
    priceIds: PriceIdApiInterface[];
}

// eslint-disable-next-line complexity
const CheckoutDetailsContainer = ({
    onApplyHundredPercentOff,
    priceIds
}: PropTypes) => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const { t } = useTranslation();

    // This is used globally
    const [coupon, setCoupon] = useAtom(couponAtom);

    const couponParam = searchParams.get("coupon");

    const [checkoutInfo, setCheckoutInfo] =
        useState<CheckoutInfoInterface | null>(null);
    const [couponInput, setCouponInput] = useState<string | null>(coupon);

    const [isCouponRemoved, setIsCouponRemoved] = useState<boolean>(false);

    const [isCouponInputCollapsed, setIsCouponInputCollapsed] = useState(false);
    const [isNoticeCollapsed, setIsNoticeCollapsed] = useState(false);
    const [shouldCouponRevalidate, setShouldCouponRevalidate] = useState(false);

    const [createSubscriptionState] = useAtom(createSubscriptionAtom);
    const [checkoutTotalAmount, setCheckoutTotalAmount] = useAtom(
        checkoutTotalAmountAtom
    );
    const [checkout] = useAtom(checkoutAtom);
    const [validateCouponState, validateCouponMutate] =
        useAtom(validateCouponAtom);
    const [pageQueryParams] = useAtom(pageQueryParamsAtom);
    const [isPaymentPending] = useAtom(isPaymentPendingAtom);

    const { routeWithQueryParams, onSetupQueryParams } =
        useCheckoutQueryParams();

    const {
        state: { checkout: checkoutAppState }
    } = useAppStateSync();

    // Internal to this component, being used to memoize the initially calculated total checkout amount
    const internalTotalAmountRef = useRef(0);
    const initialCouponValidatedRef = useRef(false);

    // Being used to calculate the width of the error message
    const couponInputRef = useRef<HTMLInputElement>(null);

    const checkoutPurchasingAddon =
        checkoutAppState?.checkoutPurchasingAddon ?? null;
    const checkoutSubjectsFee = checkoutAppState?.checkoutSubjectsFee ?? null;

    const onChangeCoupon = (e: ChangeEvent<HTMLInputElement>) => {
        validateCouponState.reset();
        const inputVal = e.target.value;
        setCouponInput(inputVal);

        if (shouldCouponRevalidate) {
            onApplyCoupon(inputVal, priceIds);
            setShouldCouponRevalidate(false);
        }
    };

    const onApplyCoupon = (
        inputVal: string | null,
        priceIds: PriceIdApiInterface[],
        e?: FormEvent
    ) => {
        e?.preventDefault();
        if (inputVal !== null)
            validateCouponMutate([
                {
                    coupon: inputVal,
                    ...(checkoutPurchasingAddon || checkoutSubjectsFee
                        ? { type: "charge" }
                        : {}),
                    ...(checkoutSubjectsFee
                        ? {
                              itemQty:
                                  checkoutAppState!.checkoutSubjectsFee!
                                      .quantity
                          }
                        : {}),

                    priceIds: checkoutPurchasingAddon
                        ? [
                              priceIds.find(i => i.item_type === "addon")!
                                  .item_price_id
                          ]
                        : checkoutSubjectsFee
                        ? [checkoutSubjectsFee.priceId]
                        : priceIds.map(i => ({
                              item_price_id: i.item_price_id
                          }))
                }
            ]);
    };

    const onRemoveCoupon = () => {
        setCouponInput("");
        setCoupon(null);
        setCheckoutTotalAmount(internalTotalAmountRef.current);
        setCheckoutInfo(prevInfo => {
            if (!prevInfo) return null;

            return {
                ...prevInfo,
                product: {
                    ...prevInfo.product,
                    isDiscountApplied: false,
                    reducedPrice: 0
                },
                addon: prevInfo.addon
                    ? {
                          ...prevInfo.addon,
                          isDiscountApplied: false,
                          reducedPrice: 0
                      }
                    : null,
                discount: null
            };
        });
        setShouldCouponRevalidate(false);
        setIsCouponRemoved(true);
        validateCouponState.reset();
        onApplyHundredPercentOff(false);
        onSetupQueryParams({
            ...pageQueryParams,
            coupon: null
        });
    };

    const goBack = () => {
        /**
         * Handling the back navigation when users coming from
         * parents portal to pay for a subject change fee
         */
        if (checkoutSubjectsFee) {
            window.location.href =
                checkoutAppState?.checkoutRedirectsToOnBackNav ??
                route("parents.dashboard");
            return;
        }
        if (
            checkout.plan?.addons &&
            // logical AND is short-circuiting
            checkout.plan.addons.every(a => !a.isIncluded)
        ) {
            navigate(
                routeWithQueryParams(CheckoutPageRoutesEnum.ADDON_SELECT_PAGE)
            );
        } else {
            // If the selected product family has one plan, the plan selection page will be skipped from showing.
            if (checkout.productFamily!.plans.length === 1) {
                navigate(
                    routeWithQueryParams(
                        CheckoutPageRoutesEnum.PRODUCT_FAMILY_SELECT_PAGE
                    )
                );
            } else {
                navigate(
                    routeWithQueryParams(
                        CheckoutPageRoutesEnum.PLAN_SELECT_PAGE
                    )
                );
            }
        }
    };

    useEffect(() => {
        const prepareCheckoutInfo = () => {
            // List out each product in cart, and calculate the total amount
            if (checkout.productFamily && checkout.plan && checkout.grade) {
                const newCheckoutInfo: CheckoutInfoInterface = {
                    product: {
                        name: checkout.productFamily.name,
                        price: checkout.plan.price,
                        grade: checkout.grade.name,
                        currency: checkout.plan.currency,
                        isEditable: true,
                        /**
                         * Users only pay for a charge, plan is already purchased
                         */
                        isAlreadyPurchased:
                            !!checkoutAppState?.checkoutPurchasingAddon ||
                            !!checkoutAppState?.checkoutSubjectsFee,
                        isDiscountApplied: false,
                        reducedPrice: 0
                    },
                    addon: (() => {
                        const defaultIncludedAddon =
                            checkout.plan.addons?.find(a => a.isIncluded) ??
                            null;
                        if (checkout.addon) {
                            return {
                                name: checkout.addon.name,
                                price: checkout.addon.price,
                                isEditable: true,
                                isDiscountApplied: false,
                                reducedPrice: 0
                            };
                        }
                        // Charge related to subject change fee
                        if (checkoutSubjectsFee) {
                            return {
                                name: `Subject change x ${checkoutSubjectsFee.quantity}`,
                                price: checkoutSubjectsFee.price,
                                isDiscountApplied: false,
                                isEditable: false,
                                reducedPrice: 0
                            };
                        }
                        if (defaultIncludedAddon) {
                            return {
                                name: defaultIncludedAddon.name,
                                price: defaultIncludedAddon.price,
                                isEditable: false,
                                isDiscountApplied: false,
                                reducedPrice: 0
                            };
                        }
                        return null;
                    })(),
                    discount: null
                };
                let totalAmount = 0;

                if (checkoutPurchasingAddon) {
                    if (checkout.addon) totalAmount += checkout.addon.price;
                    setCheckoutTotalAmount(totalAmount);
                    setCheckoutInfo(newCheckoutInfo);
                    // Total amount is initially updated in the state, and also momoized
                    internalTotalAmountRef.current = totalAmount;
                    return;
                }

                if (checkoutSubjectsFee) {
                    if (checkoutSubjectsFee.price)
                        totalAmount += checkoutSubjectsFee.price;
                    setCheckoutTotalAmount(totalAmount);
                    setCheckoutInfo(newCheckoutInfo);
                    // Total amount is initially updated in the state, and also momoized
                    internalTotalAmountRef.current = totalAmount;
                    return;
                }

                totalAmount +=
                    checkout.plan.price +
                    (checkout.addon ? checkout.addon.price : 0);
                setCheckoutTotalAmount(totalAmount);
                setCheckoutInfo(newCheckoutInfo);
                // Total amount is initially updated in the state, and also memoized
                internalTotalAmountRef.current = totalAmount;
            }
        };

        prepareCheckoutInfo();
    }, [checkout]);

    /**
     * "useEffect" hook to apply coupon passed via query parameter automatically.
     */
    useEffect(() => {
        if (
            couponParam &&
            priceIds.length > 0 &&
            !initialCouponValidatedRef.current &&
            !isCouponRemoved
        ) {
            setCoupon(couponParam);
            setCouponInput(couponParam);
            setIsCouponInputCollapsed(true);
            onApplyCoupon(couponParam, priceIds);

            initialCouponValidatedRef.current = true;
        }
    }, [couponParam, priceIds]);

    useEffect(() => {
        const updateCheckoutInfo = () => {
            // Resetting the checkout info values
            const newCheckoutInfo = {
                product: {
                    ...checkoutInfo?.product,
                    isDiscountApplied: false,
                    reducedPrice: 0
                },
                addon:
                    checkout.addon || !!checkoutSubjectsFee
                        ? {
                              ...checkoutInfo?.addon,
                              isDiscountApplied: false,
                              reducedPrice: 0
                          }
                        : null,
                discount: null
            } as CheckoutInfoInterface;

            if (validateCouponState.isSuccess) {
                const { data } = validateCouponState.data;
                if (data.discountAppliedOn === "each_specified_item") {
                    data.items.forEach(item => {
                        if (item.itemType === "plan" && item.isDiscountAdded) {
                            newCheckoutInfo.product.isDiscountApplied = true;
                            newCheckoutInfo.product.reducedPrice =
                                item.discountedPrice;
                        }

                        if (item.itemType === "addon" && item.isDiscountAdded) {
                            newCheckoutInfo.addon!.isDiscountApplied = true;
                            newCheckoutInfo.addon!.reducedPrice =
                                item.discountedPrice;
                        }
                    });
                }

                const discount: CheckoutSummaryDiscount = {
                    name: data.name,
                    description: data.description,
                    invoiceName: data.invoiceName,
                    code: data.id,
                    discountPercentage: data.discountPercentage,
                    discountType: data.discountType,
                    discountAmount: data.discountPrice,
                    durationType: data.durationType,
                    durationLimit: data.durationLimit,
                    appliedOn: data.discountAppliedOn,
                    totalReducedPrice: data.discountedPrice,
                    isEditable: true
                };
                newCheckoutInfo["discount"] = discount;

                setCheckoutInfo(newCheckoutInfo);
                setCheckoutTotalAmount(data.totalDiscountedPrice);
                setCoupon(couponInput);
                setShouldCouponRevalidate(true);
                onApplyHundredPercentOff(data.totalDiscountedPrice <= 0);
                initialCouponValidatedRef.current = true;
                setIsCouponRemoved(false);

                // Update the related query parameters with the selected coupon.
                onSetupQueryParams({
                    ...pageQueryParams,
                    coupon: data.id
                });
            }

            if (validateCouponState.isError) {
                onApplyHundredPercentOff(false);
                setCheckoutTotalAmount(internalTotalAmountRef.current);
                setCheckoutInfo(newCheckoutInfo);
                setCoupon(null);
                setIsCouponRemoved(false);

                if (window.heap?.track) {
                    window.heap.track("Payment Error", {
                        error: "Invalid Coupon",
                        coupon: couponInput
                    });
                }

                // Update the related query parameters with the selected coupon.
                onSetupQueryParams({
                    ...pageQueryParams,
                    coupon: null
                });
            }
        };

        updateCheckoutInfo();
    }, [validateCouponState, couponInput]);

    const getProgramAndRenewalNote = useMemo(() => {
        // Yearly Plan - Upfront notes
        const yearlyPlanUpfrontNote = () => {
            if (checkout.grade?.upgradeAllowed) {
                return checkout.addon
                    ? t(
                          "checkoutDetails.yearlyPlanUpfrontNoteWithAddonAutoRenew",
                          "Your program and premium services will automatically renew annually. You have the option to cancel auto-renewal at anytime."
                      )
                    : t(
                          "checkoutDetails.yearlyPlanUpfrontNoteWithoutAddonAutoRenew",
                          "Your program will automatically renew annually. You have the option to cancel auto-renewal at anytime."
                      );
            }
            return checkout.addon
                ? t(
                      "checkoutDetails.yearlyPlanUpfrontNoteWithAddonNonRenew",
                      "This is a one-time payment and your program and premium services will not automatically renew at the end of your annual plan period."
                  )
                : t(
                      "checkoutDetails.yearlyPlanUpfrontNoteWithoutAddonNonRenew",
                      "This is a one-time payment and your program will not automatically renew at the end of your annual plan period."
                  );
        };

        // Yearly Plan - Installment notes
        const yearlyPlanInstallmentNote = () => {
            if (checkout.grade?.upgradeAllowed) {
                return checkout.addon
                    ? t(
                          "checkoutDetails.yearlyPlanInstallmentNoteWithAddonAutoRenew",
                          "As you’ve selected an instalment plan, you agree and commit to pay the full plan amount through the scheduled instalment payments shown above until the full amount has been paid. Once paid, your program and premium services will automatically renew annually on an instalment plan. You have the option to cancel auto-renewal at anytime."
                      )
                    : t(
                          "checkoutDetails.yearlyPlanInstallmentNoteWithoutAddonAutoRenew",
                          "As you’ve selected an instalment plan, you agree and commit to pay the full plan amount through the scheduled instalment payments shown above until the full amount has been paid. Once paid, your program will automatically renew annually on an instalment plan. You have the option to cancel auto-renewal at anytime."
                      );
            }
            return checkout.addon
                ? t(
                      "checkoutDetails.yearlyPlanInstallmentNoteWithAddonNonRenew",
                      "As you’ve selected an instalment plan, you agree and commit to pay the full plan amount through the scheduled instalment payments shown above until the full amount has been paid. Your program and premium services will not automatically renew at the end of your annual plan period."
                  )
                : t(
                      "checkoutDetails.yearlyPlanInstallmentNoteWithoutAddonNonRenew",
                      "As you’ve selected an instalment plan, you agree and commit to pay the full plan amount through the scheduled instalment payments shown above until the full amount has been paid. Your program will not automatically renew at the end of your annual plan period."
                  );
        };

        // Term Plan - Upfront notes
        const termPlanUpfrontNote = () => {
            return t(
                "checkoutDetails.termPlanUpfrontNote",
                "Your program will automatically renew every 90 days until the last term of a grade group, i.e. Grade 6 Term 4 (Primary) or Grade 10 Term 4 (Secondary). You have the option to cancel auto-renewal at anytime."
            );
        };

        if (checkout.plan?.type === PLAN_TYPE.YEARLY) {
            return yearlyPlanUpfrontNote();
        } else if (checkout.plan?.type === PLAN_TYPE.INSTALLMENT) {
            return yearlyPlanInstallmentNote();
        } else if (checkout.plan?.type === PLAN_TYPE.QUARTERLY) {
            return termPlanUpfrontNote();
        }
    }, [checkout]);

    return (
        <Flex
            bg={["white", null, null, "gray.50"]}
            direction="column"
            w="full"
            align="start"
            px={[0, null, null, 18, 20]}
        >
            <Flex
                px={3}
                gap={2}
                w="full"
                align="center"
                justify={["center", null, "start"]}
                mt={10}
            >
                <Box
                    className={ClassesEnum.Button}
                    data-tracking-id={getTrackingId(
                        [
                            ClassesEnum.PaymentDetailsContainer,
                            ClassesEnum.Button
                        ],
                        "goBackFromPaymentPage"
                    )}
                    onClick={goBack}
                    cursor="pointer"
                    mt={-2}
                >
                    <FontAwesomeIcon
                        icon={faChevronLeft}
                        color="var(--euka-colors-gray-900)"
                    />
                </Box>
                <Text m={["auto", null, 0]} textAlign="center" type="heading">
                    {t("checkout.checkoutPage.title", "Payment Details")}
                </Text>
            </Flex>
            <Flex
                border="1px solid"
                borderLeftWidth={[0, null, null, "1px"]}
                borderRightWidth={[0, null, null, "1px"]}
                borderColor="gray.400"
                direction="column"
                gap={4}
                py={8}
                px={7}
                bg="white"
                borderRadius={[0, null, null, 15]}
                w={["full", null, null, null, null, 700]}
                mt={8}
                ml={[0, null, "auto"]}
            >
                {checkoutInfo && (
                    <CheckoutSummary
                        product={checkoutInfo.product}
                        addon={checkoutInfo.addon}
                        discount={checkoutInfo.discount}
                        onRemoveCoupon={onRemoveCoupon}
                        addonSectionTitle={
                            checkoutSubjectsFee
                                ? t(
                                      "checkoutDetails.programChange",
                                      "Program change"
                                  )
                                : t(
                                      "checkoutDetails.premiumServices",
                                      "Premium Services"
                                  )
                        }
                        haveExternalAddons={!!checkoutSubjectsFee}
                        plan={checkout?.plan}
                    />
                )}
                <Flex justify="space-between">
                    <Flex direction="column">
                        <Text
                            fontSize={18}
                            type="subHeading"
                            fontFamily="inter, sans-serif"
                        >
                            {t("checkout.checkoutPage.totalCost", "Total Cost")}
                        </Text>
                    </Flex>
                    <Flex
                        mt="auto"
                        justify="center"
                        align="center"
                        gap={4}
                        mr={7}
                    >
                        <Text
                            className={ClassesEnum.Text}
                            data-tracking-id={getTrackingId(
                                [
                                    ClassesEnum.PaymentDetailsContainer,
                                    ClassesEnum.Text
                                ],
                                "totalAmount"
                            )}
                            fontSize={18}
                            type="subHeading"
                            fontFamily="inter, sans-serif"
                        >
                            {formatPrice(
                                checkoutTotalAmount,
                                checkout?.plan?.currency
                            )}
                        </Text>
                    </Flex>
                </Flex>
                {!checkoutSubjectsFee && (
                    <>
                        <Text
                            lineHeight="15px"
                            color="gray.600"
                            fontSize={12}
                            data-testid="program-and-renewal-note"
                        >
                            {getProgramAndRenewalNote}
                        </Text>
                        <Text
                            lineHeight="15px"
                            color="gray.600"
                            fontSize={12}
                            data-testid="program-refund-note"
                        >
                            {t(
                                "checkoutDetails.refundNote",
                                "New customers are eligible for a full refund, minus a processing fee, if they cancel their program within the first 7 days."
                            )}
                        </Text>
                        <Text
                            lineHeight="15px"
                            color="gray.600"
                            fontSize={12}
                            data-testid="program-access-note"
                        >
                            {t(
                                "checkoutDetails.programLicenseNote",
                                "Access to your learning program will be based on your purchase date and your plan length (365 days for an annual plan)"
                            )}
                        </Text>
                    </>
                )}

                <Collapsable
                    data-testid="input-collapsable"
                    isCollapsed={isCouponInputCollapsed}
                    onToggleCollapse={() =>
                        setIsCouponInputCollapsed(!isCouponInputCollapsed)
                    }
                    title={t(
                        "checkoutDetails.addCouponCode",
                        "Add your coupon code"
                    )}
                    data-tracking-id={getTrackingId(
                        [ClassesEnum.PaymentDetailsContainer],
                        "addYourCouponCode"
                    )}
                >
                    <Box
                        as="form"
                        onSubmit={(e: FormEvent) =>
                            onApplyCoupon(couponInput, priceIds, e)
                        }
                    >
                        <FormControl
                            className={ClassesEnum.FormField}
                            data-tracking-id={getTrackingId(
                                [
                                    ClassesEnum.PaymentDetailsContainer,
                                    ClassesEnum.FormField
                                ],
                                "inputCouponCode"
                            )}
                        >
                            <FormLabel fontSize={14}>
                                {t(
                                    "checkout.checkoutPage.couponLabel",
                                    "Coupon code"
                                )}
                            </FormLabel>
                            <Flex direction="column" gap={2}>
                                <Flex gap={2} mb={1} mx={1}>
                                    <InputGroup>
                                        <Input
                                            data-testid="coupon-input"
                                            className={ClassesEnum.Input}
                                            data-tracking-id={getTrackingId(
                                                [
                                                    ClassesEnum.PaymentDetailsContainer,
                                                    ClassesEnum.FormField,
                                                    ClassesEnum.Input
                                                ],
                                                "inputCouponCode"
                                            )}
                                            value={couponInput ?? ""}
                                            ref={couponInputRef}
                                            isInvalid={
                                                couponInput !== "" &&
                                                validateCouponState.isError
                                            }
                                            onChange={onChangeCoupon}
                                            color={
                                                validateCouponState.isSuccess
                                                    ? "mintGreen.500"
                                                    : "gray.800"
                                            }
                                            aria-success={
                                                validateCouponState.isSuccess
                                            }
                                            autoFocus
                                            isDisabled={
                                                isPaymentPending ||
                                                createSubscriptionState.isSuccess ||
                                                validateCouponState.isPending
                                            }
                                        />
                                        {!validateCouponState.isPending && (
                                            <InputRightElement>
                                                {validateCouponState.isSuccess && (
                                                    <FontAwesomeIcon
                                                        icon={faCircleCheck}
                                                        color="var(--euka-colors-mintGreen-500"
                                                        fontSize={17}
                                                    />
                                                )}
                                                {couponInput &&
                                                    validateCouponState.isError && (
                                                        <FontAwesomeIcon
                                                            onClick={() =>
                                                                setCouponInput(
                                                                    ""
                                                                )
                                                            }
                                                            cursor="pointer"
                                                            icon={faCircleXmark}
                                                            color="var(--euka-colors-sunsetRed-500"
                                                            fontSize={17}
                                                        />
                                                    )}
                                            </InputRightElement>
                                        )}
                                    </InputGroup>
                                    <Button
                                        className={ClassesEnum.Button}
                                        data-tracking-id={getTrackingId(
                                            [
                                                ClassesEnum.PaymentDetailsContainer,
                                                ClassesEnum.Button
                                            ],
                                            "applyCouponCode"
                                        )}
                                        size="sm"
                                        colorScheme="ebonyClay"
                                        mt="auto"
                                        w={32}
                                        isDisabled={
                                            !couponInput ||
                                            isPaymentPending ||
                                            createSubscriptionState.isSuccess
                                        }
                                        isLoading={
                                            validateCouponState.isPending
                                        }
                                        type="submit"
                                    >
                                        Apply
                                    </Button>
                                </Flex>
                                {couponInput && validateCouponState.isError && (
                                    <Alert
                                        type="error"
                                        message={
                                            (
                                                validateCouponState.error as {
                                                    response: {
                                                        data: {
                                                            message: string;
                                                        };
                                                    };
                                                }
                                            ).response.data.message
                                        }
                                        w={
                                            couponInputRef.current
                                                ? couponInputRef.current
                                                      .offsetWidth
                                                : "100%"
                                        }
                                    />
                                )}
                            </Flex>
                        </FormControl>
                    </Box>
                </Collapsable>
            </Flex>
            {!checkoutSubjectsFee && (
                <Box
                    borderBottom="1px solid"
                    borderColor={["gray.400", null, null, "transparent"]}
                    ml={[0, null, "auto"]}
                    w={["full", null, null, null, null, 700]}
                    py={6}
                    px={[4, null]}
                >
                    <Collapsable
                        isCollapsed={isNoticeCollapsed}
                        onToggleCollapse={() =>
                            setIsNoticeCollapsed(!isNoticeCollapsed)
                        }
                        title={
                            <Text color="royalblue">
                                {t(
                                    "checkoutDetails.additionalStudentTitle",
                                    "Want to add another student?"
                                )}
                            </Text>
                        }
                    >
                        <Text mb={4} fontSize={14}>
                            {t(
                                "checkoutDetails.additionalStudentNote",
                                "If you'd like to enrol additional students, simply complete the purchase and enrolment for your first student, and once you log into your Parent Portal, you'll see the option to add more students."
                            )}
                        </Text>
                    </Collapsable>
                </Box>
            )}
        </Flex>
    );
};

export default CheckoutDetailsContainer;
